import { useState } from 'react'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Archive, Pencil, Send, Star } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminQuestionsListQueryKey,
  getV1QuizAdminQuestionsRetrieveQueryKey,
  useV1QuizAdminCapabilities,
  useV1QuizAdminQuestionsModulesPartialUpdate,
  useV1QuizAdminQuestionsPublishCreate,
  useV1QuizAdminQuestionsRetireCreate,
  useV1QuizAdminQuestionsRetrieve,
  useV1QuizAdminQuestionsReviseCreate,
  useV1QuizAdminQuestionsTopicsPartialUpdate,
} from '@/api/client'
import type {
  AdminQuestionReviseRequest,
  AdminQuestionRevision,
  QuizContentModuleEnum,
} from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { ModulePicker } from '@/components/quiz-bank/module-picker'
import { TopicPicker, type TopicSelection } from '@/components/quiz-bank/topic-picker'
import { QuestionStatusBadge } from '@/components/quiz-bank/question-status-badge'
import { QuestionForm } from '@/components/quiz-bank/question-form'
import { QuizCapabilityNotice } from '@/components/quiz/quiz-capability-notice'
import {
  canAuthorQuiz,
  canPublishQuiz,
  quizCapabilitiesOrClosed,
} from '@/components/quiz/quiz-capabilities'
import { questionActionError } from '@/components/quiz-bank/question-error'
import { fromRevision } from '@/components/quiz-bank/question-form-schema'
import { RevisionContent, RevisionHistory } from '@/components/quiz-bank/revision-history'
import { moduleLabel } from '@/lib/activation-labels'
import { difficultyLabel, poolLabel, typeLabel } from '@/lib/quiz-labels'
import { headRevision, questionExcerpt } from '@/lib/quiz-excerpt'

export const Route = createFileRoute('/_panel/pytania/$uid')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin, broader than konta-administracyjne):
    // everyone else is bounced out before any question data loads (FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: QuestionDetailPage,
})

type ActionKind = 'publish' | 'retire' | null

/**
 * US2/US3 question detail (T019–T021): header + metadata, the head revision's
 * content incl. the answer key (operator-only surface), edit-as-new-revision,
 * dialog-gated publish/retire, and the full revision history. There is NO
 * delete affordance anywhere — the lifecycle ends at retired (FR-014).
 */
function QuestionDetailPage() {
  const { uid } = Route.useParams()
  const queryClient = useQueryClient()
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canAuthor = canAuthorQuiz(capabilities)
  const canPublish = canPublishQuiz(capabilities)

  const query = useV1QuizAdminQuestionsRetrieve(uid)
  const question = query.data?.status === 200 ? query.data.data : undefined

  const [editing, setEditing] = useState(false)
  const [action, setAction] = useState<ActionKind>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [editingModules, setEditingModules] = useState(false)
  const [moduleDraft, setModuleDraft] = useState<string[]>([])
  const [moduleError, setModuleError] = useState<string | null>(null)
  const [editingTopics, setEditingTopics] = useState(false)
  const [topicDraft, setTopicDraft] = useState<TopicSelection>({ topics: [], primaryTopic: '' })
  const [topicError, setTopicError] = useState<string | null>(null)

  const reviseMutation = useV1QuizAdminQuestionsReviseCreate()
  const publishMutation = useV1QuizAdminQuestionsPublishCreate()
  const retireMutation = useV1QuizAdminQuestionsRetireCreate()
  const modulesMutation = useV1QuizAdminQuestionsModulesPartialUpdate()
  const topicsMutation = useV1QuizAdminQuestionsTopicsPartialUpdate()
  const pending = publishMutation.isPending || retireMutation.isPending

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: getV1QuizAdminQuestionsRetrieveQueryKey(uid),
    })
    await queryClient.invalidateQueries({ queryKey: getV1QuizAdminQuestionsListQueryKey() })
  }

  const saveModules = async () => {
    setModuleError(null)
    const response = await modulesMutation.mutateAsync({
      uid,
      data: { modules: moduleDraft as QuizContentModuleEnum[] },
    })
    if (response.status >= 400) {
      setModuleError(questionActionError(response.data, response.status))
      return
    }
    await refresh()
    setEditingModules(false)
  }

  const saveTopics = async () => {
    setTopicError(null)
    const response = await topicsMutation.mutateAsync({
      uid,
      data: { topics: topicDraft.topics, primary_topic: topicDraft.primaryTopic },
    })
    if (response.status >= 400) {
      setTopicError(questionActionError(response.data, response.status))
      return
    }
    await refresh()
    setEditingTopics(false)
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Szczegóły pytania" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!question) {
    return (
      <>
        <PageHeader title="Szczegóły pytania" />
        <div className="flex flex-col items-start gap-3 p-6">
          <p className="text-sm text-muted-foreground">Nie znaleziono pytania.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/pytania">
              <ArrowLeft /> Wróć do banku pytań
            </Link>
          </Button>
        </div>
      </>
    )
  }

  const head: AdminQuestionRevision | null = headRevision(question)
  // Runtime caveat (plan.md): `current_revision` is null on the wire for a
  // never-published draft despite the generated non-nullable type — guard it.
  const inCirculation: AdminQuestionRevision | null = question.current_revision ?? null
  const hasUnpublishedHead = head !== null && !head.is_published
  const isRetired = question.status === 'retired'
  const isPublished = question.status === 'published'
  const editPrefill = head && !isRetired ? fromRevision(question.type, head) : null

  const closeAction = () => {
    setAction(null)
    setActionError(null)
  }

  const handleRevise = async (payload: AdminQuestionReviseRequest) => {
    // Submitting the edit form creates a NEW head revision server-side (O3);
    // history and the in-circulation revision stay untouched until publish.
    const response = await reviseMutation.mutateAsync({ uid, data: payload })
    if (response.status < 400) {
      await refresh()
      setEditing(false)
    }
    return response
  }

  const runPublish = async () => {
    setActionError(null)
    const response = await publishMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(questionActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runRetire = async () => {
    setActionError(null)
    const response = await retireMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(questionActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  const topicsCard = (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Tematy</CardTitle>
        {!editingTopics && canAuthor && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTopicDraft({
                topics: (question.topics ?? []).map((t) => t.slug),
                primaryTopic: (question.topics ?? []).find((t) => t.is_primary)?.slug ?? '',
              })
              setTopicError(null)
              setEditingTopics(true)
            }}
          >
            <Pencil /> Edytuj
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editingTopics ? (
          <div className="flex flex-col gap-3">
            <TopicPicker
              value={topicDraft.topics}
              onChange={(topics) => setTopicDraft((current) => ({ ...current, topics }))}
              primaryTopic={topicDraft.primaryTopic}
              onPrimaryTopicChange={(primaryTopic) =>
                setTopicDraft((current) => ({ ...current, primaryTopic }))
              }
              disabled={topicsMutation.isPending}
            />
            {topicError && <p className="text-sm text-destructive">{topicError}</p>}
            <div className="flex gap-2">
              <Button size="sm" onClick={saveTopics} disabled={topicsMutation.isPending}>
                Zapisz
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingTopics(false)
                  setTopicError(null)
                }}
                disabled={topicsMutation.isPending}
              >
                Anuluj
              </Button>
            </div>
          </div>
        ) : question.topics && question.topics.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {question.topics.map((topic) => (
              <Badge key={topic.slug} variant={topic.is_primary ? 'default' : 'secondary'}>
                {topic.is_primary && <Star className="size-3 fill-current" />}
                {topic.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Brak przypisanych tematów.</p>
        )}
      </CardContent>
    </Card>
  )

  const modulesCard = (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Moduły</CardTitle>
        {!editingModules && canAuthor && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setModuleDraft([...(question.modules ?? [])])
              setModuleError(null)
              setEditingModules(true)
            }}
          >
            <Pencil /> Edytuj
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editingModules ? (
          <div className="flex flex-col gap-3">
            <ModulePicker
              value={moduleDraft}
              onChange={setModuleDraft}
              disabled={modulesMutation.isPending}
            />
            {moduleError && <p className="text-sm text-destructive">{moduleError}</p>}
            <div className="flex gap-2">
              <Button size="sm" onClick={saveModules} disabled={modulesMutation.isPending}>
                Zapisz
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingModules(false)
                  setModuleError(null)
                }}
                disabled={modulesMutation.isPending}
              >
                Anuluj
              </Button>
            </div>
          </div>
        ) : question.modules && question.modules.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {question.modules.map((m) => (
              <Badge key={m} variant="secondary">
                {moduleLabel(m)}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Brak przypisanych modułów.</p>
        )}
      </CardContent>
    </Card>
  )

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/pytania">
                <ArrowLeft />
              </Link>
            </Button>
            <span className="max-w-xl truncate">{questionExcerpt(question)}</span>
            <QuestionStatusBadge status={question.status} />
          </span>
        }
        actions={
          !editing && (
            <span className="flex gap-2">
              {!isRetired && canAuthor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  disabled={editPrefill === null}
                >
                  <Pencil /> Edytuj
                </Button>
              )}
              {hasUnpublishedHead && !isRetired && canPublish && (
                <Button
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('publish')
                  }}
                >
                  <Send /> Opublikuj
                </Button>
              )}
              {isPublished && canAuthor && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('retire')
                  }}
                >
                  <Archive /> Wycofaj
                </Button>
              )}
            </span>
          )
        }
      />

      <div className="flex max-w-4xl flex-col gap-6 p-6">
        <QuizCapabilityNotice capabilities={capabilities} />
        {isRetired && (
          <p className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
            Pytanie jest wycofane — nie można go edytować ani publikować. Historia rewizji pozostaje
            dostępna.
          </p>
        )}

        {hasUnpublishedHead && !isRetired && !editing && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Istnieje nieopublikowana wersja robocza (rewizja {head.number}).
            {inCirculation
              ? ` Uczniowie otrzymują nadal rewizję ${inCirculation.number}.`
              : ' Pytanie nie trafiło jeszcze do obiegu.'}
          </p>
        )}

        {editing && head && canAuthor ? (
          <Card>
            <CardHeader>
              <CardTitle>Edycja — nowa rewizja</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                Zapis utworzy nową, nieopublikowaną rewizję ({head.number + 1}). Opublikowane
                rewizje pozostają niezmienione.
              </p>
              <QuestionForm
                type={question.type}
                mode="revise"
                initial={editPrefill ?? undefined}
                submitLabel="Zapisz nową rewizję"
                onSubmit={handleRevise}
                onCancel={() => setEditing(false)}
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Informacje</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Row label="Typ" value={typeLabel(question.type)} />
              <Row label="Poziom trudności" value={head ? difficultyLabel(head.difficulty) : '—'} />
              <Row label="Pula" value={question.pool ? poolLabel(question.pool) : '—'} />
              <Row
                label="W obiegu"
                value={
                  inCirculation
                    ? `Rewizja ${inCirculation.number}`
                    : 'Brak (nigdy nie opublikowano)'
                }
              />
              <Row
                label="Identyfikator"
                value={<span className="font-mono text-xs">{question.uid}</span>}
              />
              <Row
                label="Utworzono"
                value={new Date(question.created_at).toLocaleString('pl-PL')}
              />
              <Row
                label="Zaktualizowano"
                value={new Date(question.updated_at).toLocaleString('pl-PL')}
              />
            </CardContent>
          </Card>
        )}

        {/* Moduły pozostają edytowalne również podczas edycji treści — to metadane
            pytania, zapisywane własnym PATCH-em, niezależnym od rewizji treści. */}
        {modulesCard}
        {topicsCard}

        {!editing && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Najnowsza treść{head ? ` (rewizja ${head.number})` : ''}</CardTitle>
              </CardHeader>
              <CardContent>
                {head ? (
                  <RevisionContent type={question.type} revision={head} />
                ) : (
                  <p className="text-sm text-muted-foreground">Brak rewizji.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Historia rewizji</CardTitle>
              </CardHeader>
              <CardContent>
                <RevisionHistory type={question.type} revisions={question.revisions} />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* US3 — publish (FR-010/FR-016) */}
      <ConfirmDialog
        open={action === 'publish'}
        onOpenChange={(open) => (open ? setAction('publish') : closeAction())}
        title="Opublikuj rewizję"
        description={
          <>
            Opublikowana zostanie najnowsza nieopublikowana rewizja
            {head ? ` (${head.number})` : ''}. Od tej chwili to ją będą otrzymywać uczniowie.
          </>
        }
        confirmLabel="Opublikuj"
        pending={pending}
        error={actionError}
        onConfirm={() => void runPublish()}
      />

      {/* US3 — retire (FR-013); destructive, but history stays (no delete, FR-014) */}
      <ConfirmDialog
        open={action === 'retire'}
        onOpenChange={(open) => (open ? setAction('retire') : closeAction())}
        title="Wycofaj pytanie"
        description={
          <>
            Pytanie zostanie wycofane z obiegu — uczniowie przestaną je otrzymywać. Historia rewizji
            zostanie zachowana, ale pytania nie będzie można już edytować ani ponownie opublikować.
          </>
        }
        confirmLabel="Wycofaj pytanie"
        destructive
        pending={pending}
        error={actionError}
        onConfirm={() => void runRetire()}
      />
    </>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  )
}
